import { IAdvancedFilterGroup } from '../../settings-new/models/general/advanced-filter-group.interface';
import {
  isGeneralAdvancedFilterGrid,
  isStockTakeAdvancedFilterGrid,
} from '../../settings-new/models/general/advanced-filter-grid.constants';
import { IStockTakeScheduleEntry } from '../models/stock-take-schedule.interface';

/**
 * Source fields for stock take advanced filter selection (single or multiple).
 */
export interface IStockTakeFilterSource {
  filterGroup?: IAdvancedFilterGroup | null;
  filterGroups?: IAdvancedFilterGroup[];
}

/**
 * Resolves the effective filter groups from single- or multi-filter fields.
 */
export function resolveStockTakeFilterGroups(source: IStockTakeFilterSource): IAdvancedFilterGroup[] {
  if (source.filterGroups?.length) {
    return source.filterGroups;
  }
  if (source.filterGroup) {
    return [source.filterGroup];
  }
  return [];
}

/**
 * Advanced filter groups sorted A–Z by display name (stable copy).
 */
export function sortAdvancedFilterGroupsAlphabetically(
  groups: IAdvancedFilterGroup[],
): IAdvancedFilterGroup[] {
  return [...groups].sort((a: IAdvancedFilterGroup, b: IAdvancedFilterGroup) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );
}

/**
 * Option keys for shared custom select `keyOrder` — pure alphabetical, no selected-first grouping.
 */
export function buildStockTakeFilterSelectKeyOrder(groups: IAdvancedFilterGroup[]): string[] {
  return sortAdvancedFilterGroupsAlphabetically(groups).map((group: IAdvancedFilterGroup) => String(group.id));
}

/**
 * Returns whether any advanced filter groups are configured.
 */
export function hasStockTakeFilterGroups(source: IStockTakeFilterSource): boolean {
  return resolveStockTakeFilterGroups(source).length > 0;
}

/**
 * Formats filter group names for display.
 */
export function formatStockTakeFilterGroupNames(source: IStockTakeFilterSource): string {
  return resolveStockTakeFilterGroups(source)
    .map((group: IAdvancedFilterGroup) => group.name)
    .join(', ');
}

/**
 * Returns true when a schedule still references General Filters and should be migrated.
 */
export function scheduleNeedsFilterMigration(
  schedule: IStockTakeScheduleEntry,
  savedFilters: IAdvancedFilterGroup[],
): boolean {
  const groups = resolveStockTakeFilterGroups(schedule);
  if (groups.length === 0) {
    return false;
  }
  const savedById = new Map<number, IAdvancedFilterGroup>(
    savedFilters.map((filter: IAdvancedFilterGroup) => [filter.id, filter]),
  );
  return groups.some((group: IAdvancedFilterGroup): boolean => {
    if (isGeneralAdvancedFilterGrid(group.grid)) {
      return true;
    }
    const saved = savedById.get(group.id);
    return !!saved && isGeneralAdvancedFilterGrid(saved.grid);
  });
}

/**
 * Unique General filter ids that need duplicating for the given schedules.
 */
export function collectGeneralFilterIdsForMigration(
  schedules: IStockTakeScheduleEntry[],
  savedFilters: IAdvancedFilterGroup[],
): number[] {
  const savedById = new Map<number, IAdvancedFilterGroup>(
    savedFilters.map((filter: IAdvancedFilterGroup) => [filter.id, filter]),
  );
  const ids = new Set<number>();
  for (const schedule of schedules) {
    if (!scheduleNeedsFilterMigration(schedule, savedFilters)) {
      continue;
    }
    for (const group of resolveStockTakeFilterGroups(schedule)) {
      const saved = savedById.get(group.id);
      const isGeneral =
        isGeneralAdvancedFilterGrid(group.grid) ||
        (!!saved && isGeneralAdvancedFilterGrid(saved.grid));
      if (isGeneral) {
        ids.add(group.id);
      }
    }
  }
  return Array.from(ids);
}

/**
 * Resolves the best source definition for a filter id during migration.
 */
export function resolveFilterDefinitionForMigration(
  filterId: number,
  schedules: IStockTakeScheduleEntry[],
  savedFilters: IAdvancedFilterGroup[],
): IAdvancedFilterGroup | null {
  const saved = savedFilters.find((filter: IAdvancedFilterGroup) => filter.id === filterId);
  if (saved) {
    return saved;
  }
  for (const schedule of schedules) {
    const match = resolveStockTakeFilterGroups(schedule).find(
      (group: IAdvancedFilterGroup) => group.id === filterId,
    );
    if (match) {
      return match;
    }
  }
  return null;
}

/**
 * For schedule runs: prefer the live Stock Take filter from Settings by id so
 * later edits apply. Fall back to the embedded snapshot (including pre-migration
 * General filters) so runs are not blocked and General edits do not affect
 * unmigrated schedules.
 */
export function resolveLiveStockTakeFilterGroups(
  source: IStockTakeFilterSource,
  savedFilters: IAdvancedFilterGroup[],
): IAdvancedFilterGroup[] {
  const stored = resolveStockTakeFilterGroups(source);
  if (stored.length === 0) {
    return [];
  }
  const savedById = new Map<number, IAdvancedFilterGroup>(
    savedFilters.map((filter: IAdvancedFilterGroup) => [filter.id, filter]),
  );
  return stored.map((group: IAdvancedFilterGroup): IAdvancedFilterGroup => {
    const live = savedById.get(group.id);
    if (live && isStockTakeAdvancedFilterGrid(live.grid)) {
      return live;
    }
    return group;
  });
}

/**
 * Remaps schedule filter groups using an old-id → new Stock Take filter map.
 */
export function remapScheduleFiltersAfterMigration(
  schedule: IStockTakeScheduleEntry,
  idMap: Map<number, IAdvancedFilterGroup>,
): Pick<IStockTakeScheduleEntry, 'filterGroups' | 'filterGroup'> {
  const remapped = resolveStockTakeFilterGroups(schedule).map(
    (group: IAdvancedFilterGroup): IAdvancedFilterGroup => idMap.get(group.id) ?? group,
  );
  return {
    filterGroups: remapped,
    filterGroup: remapped.length === 1 ? remapped[0] : null,
  };
}

/**
 * Returns schedules that reference the given advanced filter id (single or multi).
 */
export function findSchedulesUsingAdvancedFilter(
  filterId: number,
  schedules: IStockTakeScheduleEntry[],
): IStockTakeScheduleEntry[] {
  return schedules.filter((schedule: IStockTakeScheduleEntry): boolean =>
    resolveStockTakeFilterGroups(schedule).some(
      (group: IAdvancedFilterGroup): boolean => group.id === filterId,
    ),
  );
}
